<template>
    <section v-if="statusCode !== 404" class="flex flex-col gap-4 border-t border-border p-6" :aria-label="t('insights.teamSuggestions.heading')" :aria-busy="isFetching">
        <h4 ref="heading" tabindex="-1" class="body-small-bold text-ink">{{ t("insights.teamSuggestions.heading") }}</h4>
        <p class="body-small text-ink-muted empty:sr-only" role="status">
            <template v-if="isFetching">{{ t("insights.teamSuggestions.loading") }}</template>
            <template v-else-if="joinState.status === 'joining'">{{ t("peopleDirectory.teamSuggestions.joining", { name: joinState.name }) }}</template>
            <template v-else-if="joinState.status === 'joined'">{{ t("peopleDirectory.teamSuggestions.joined", { name: joinState.name }) }}</template>
        </p>
        <template v-if="!isFetching">
            <div v-if="error || !data" class="flex flex-col items-start gap-3">
                <p role="alert" class="body-small text-ink">
                    {{ statusCode === 429 ? t("peopleDirectory.teamSuggestions.rateLimited") : t("insights.teamSuggestions.loadError") }}
                </p>
                <BaseButton theme="secondary" size="sm" @click="retry">{{ t("common.tryAgain") }}</BaseButton>
            </div>
            <template v-else>
                <div v-if="data.degraded" class="flex flex-col items-start gap-3">
                    <p class="body-small text-ink-muted">{{ t("insights.teamSuggestions.degraded") }}</p>
                    <BaseButton theme="secondary" size="sm" @click="retry">{{ t("common.tryAgain") }}</BaseButton>
                </div>
                <ul v-if="suggestions.length" class="flex flex-col gap-4 fs-mask">
                    <li v-for="suggestion in suggestions" :key="suggestion.team_id" ref="rows" :data-team-id="suggestion.team_id" class="flex flex-col gap-2">
                        <div class="flex flex-wrap items-center justify-between gap-3">
                            <p class="body-serif text-ink min-w-0 break-words">{{ suggestion.name }}</p>
                            <div v-if="joinedTeams.has(suggestion.team_id)" class="flex items-center gap-2">
                                <span class="body-small text-ink-muted">{{ t("insights.teamSuggestions.joined") }}</span>
                                <BaseLink
                                    :href="teamUrl(suggestion.team_id)"
                                    data-team-link
                                    class="base-link body-small"
                                    :aria-label="t('personTeams.viewNamedTeam', { name: suggestion.name })">
                                    {{ t("insights.teamSuggestions.viewTeam") }}
                                </BaseLink>
                            </div>
                            <BaseButton
                                v-else
                                theme="neutral"
                                size="sm"
                                :disabled="joinState.status === 'joining'"
                                :aria-label="t('personTeams.joinNamedTeam', { name: suggestion.name })"
                                @click="joinTeam(suggestion)">
                                {{ t("personTeams.join") }}
                            </BaseButton>
                        </div>
                        <p class="body-small text-ink-muted break-words">{{ suggestion.reason }}</p>
                        <p v-if="joinState.status === 'failed' && joinState.teamId === suggestion.team_id" role="alert" class="body-small text-ink">
                            {{ t("peopleDirectory.teamSuggestions.joinError") }}
                        </p>
                    </li>
                </ul>
                <p v-else class="body-small text-ink-muted">{{ t("insights.teamSuggestions.empty") }}</p>
            </template>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ExistingTeamRecommendation, PersonTeam, TeamRecommendations } from "~vue/api/generated/team-perspective";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { TEAM_INTERACTION_SOURCE, teamInteractionRequest } from "~vue/Teams/teamInteraction";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ teamUrlTemplate: string }>();
const { t } = useI18n();
const heading = useTemplateRef<HTMLElement>("heading");
const rows = useTemplateRef<HTMLElement[]>("rows");
const { data, error, statusCode, isFetching, execute, abort } = useFetch("/v1/teams/recommendations", { immediate: false }).post({}).json<TeamRecommendations>();
const suggestions = computed(() => data.value?.recommendations.filter((suggestion) => suggestion.kind === "join").slice(0, 3) ?? []);
const joinedTeams = ref(new Map<string, PersonTeam>());
const joinState = ref<{ status: "idle" } | { status: "joining" | "joined"; teamId: string; name: string } | { status: "failed"; teamId: string }>({ status: "idle" });
const joinUrl = ref("");
const {
    data: joinedTeam,
    error: joinError,
    execute: join,
    abort: abortJoin,
} = useFetch(joinUrl, teamInteractionRequest(TEAM_INTERACTION_SOURCE.TEAM_SUGGESTION), { immediate: false }).post({}).json<PersonTeam>();

function teamUrl(teamId: string) {
    return joinedTeams.value.get(teamId)?.url ?? props.teamUrlTemplate.replace("{team_id}", teamId);
}

async function retry() {
    heading.value?.focus();
    await execute();
}

async function joinTeam(suggestion: ExistingTeamRecommendation) {
    if (joinState.value.status === "joining" || joinedTeams.value.has(suggestion.team_id)) {
        return;
    }
    const row = rows.value?.find((element) => element.dataset.teamId === suggestion.team_id);
    const button = row?.querySelector<HTMLButtonElement>("button");
    const restoreFocus = document.activeElement === button;
    joinState.value = { status: "joining", teamId: suggestion.team_id, name: suggestion.name };
    joinUrl.value = suggestion.join_url;
    await join();
    if (joinError.value || !joinedTeam.value?.is_member) {
        joinState.value = { status: "failed", teamId: suggestion.team_id };
    } else {
        joinedTeams.value.set(suggestion.team_id, joinedTeam.value);
        joinState.value = { status: "joined", teamId: suggestion.team_id, name: joinedTeam.value.name };
    }
    await nextTick();
    if (restoreFocus && (document.activeElement === document.body || document.activeElement === button)) {
        (row?.querySelector<HTMLElement>("[data-team-link]") ?? button)?.focus();
    }
}

onMounted(() => execute());
onBeforeUnmount(() => {
    abort();
    abortJoin();
});
</script>
